import { Paintbrush, Copy } from "lucide-react";
import { Page } from "@/lib/types";
import { DEFAULT_STYLE, ACCENT_PRESETS, BG_PRESETS, PageStyle, STYLE_KEYS } from "@/lib/pageStyle";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ThemePicker } from "./ThemePicker";

interface Props { page: Page; onChange: (p: Page) => void; onApplyToAll?: (style: PageStyle) => void; disabled?: boolean }

function ColorField({ label, value, presets, onChange, disabled, testid }: any) {
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <div className="mt-1 flex items-center gap-1.5">
        <label className="relative h-7 w-9 shrink-0 overflow-hidden rounded border border-gray-300" style={{ background: value }} title="Custom colour">
          <input type="color" data-testid={testid} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
        </label>
        <div className="flex flex-wrap gap-1">
          {presets.map((c: string) => <button key={c} type="button" disabled={disabled} data-testid={`${testid}-preset-${c.slice(1)}`} onClick={() => onChange(c)} className={`h-5 w-5 rounded-full border ${value === c ? "ring-2 ring-blue-600 ring-offset-1" : "border-gray-300"}`} style={{ background: c }} />)}
        </div>
      </div>
    </div>
  );
}

export function StyleControls({ page, onChange, onApplyToAll, disabled }: Props) {
  const s = { ...DEFAULT_STYLE, ...(page.style || {}) };
  const set = (k: keyof PageStyle, v: string) => onChange({ ...page, style: { ...(page.style || {}), [k]: v } });
  const reset = () => onChange({ ...page, style: {} });
  const isDefault = STYLE_KEYS.every((k) => !page.style?.[k] || page.style[k] === DEFAULT_STYLE[k]);

  return (
    <section className="min-w-0 rounded-lg border border-gray-200 p-3" data-testid="style-controls">
      <div className="flex items-center justify-between"><div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900"><Paintbrush size={13} className="text-blue-700" />Page style</div>
        {!isDefault && <button type="button" onClick={reset} disabled={disabled} className="text-[11px] text-gray-500 hover:text-gray-900" data-testid="style-reset-btn">Reset</button>}</div>
      <div className="mt-3 space-y-3">
        <ColorField label="Accent colour" value={s.accent_color} presets={ACCENT_PRESETS} onChange={(v: string) => set("accent_color", v)} disabled={disabled} testid="style-accent-color" />
        <ColorField label="Page background" value={s.bg_color} presets={BG_PRESETS} onChange={(v: string) => set("bg_color", v)} disabled={disabled} testid="style-bg-color" />
        <ColorField label="Text colour" value={s.text_color} presets={["#1c1917", "#fafaf9", "#374151", "#fef3c7"]} onChange={(v: string) => set("text_color", v)} disabled={disabled} testid="style-text-color" />
      </div>
      {onApplyToAll && <Button type="button" size="sm" variant="outline" disabled={disabled} className="mt-3 w-full text-xs" data-testid="style-apply-all-btn" onClick={() => onApplyToAll(page.style || {})}><Copy size={12} className="mr-1.5" />Apply this style to all pages</Button>}
      {onApplyToAll && <div className="mt-3"><ThemePicker currentStyle={{ ...DEFAULT_STYLE, ...(page.style || {}) }} onApply={onApplyToAll} disabled={disabled} /></div>}
    </section>
  );
}
